import React, { useEffect, useRef } from "react";

interface RangeInputProps {
  name: string;
  id: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  label: string;
  disabled?: boolean;
  /** Suppresses the min/max end-labels beneath the track. Defaults to false (labels shown). */
  hideLimits?: boolean;
  onChange: (value: number) => void;
  /** Fired once on a commit gesture (pointer-up / key-up), not per tick. Optional — existing
   *  callers that only need the live value can omit it. */
  onCommit?: (value: number) => void;
}

export const RangeInput: React.FC<RangeInputProps> = ({
  name,
  id,
  value,
  min = 0,
  max = 0,
  step = 1,
  label,
  disabled,
  hideLimits = false,
  onChange,
  onCommit,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const normalizedValue = value ?? 0;

  useEffect(() => {
    if (inputRef.current && max > min) {
      if (normalizedValue === 0) {
        inputRef.current.style.backgroundSize = "0% 100%";
        return;
      }
      const percent = ((normalizedValue - min) / (max - min)) * 100;
      inputRef.current.style.backgroundSize = `${percent}% 100%`;
    }
  }, [normalizedValue, min, max]);

  let ownClassName = [
    "w-full h-sm rounded-full appearance-none bg-secondary-200 cursor-pointer",
    "[&::-webkit-slider-thumb]:appearance-none",
    "[&::-webkit-slider-thumb]:w-lg",
    "[&::-webkit-slider-thumb]:h-lg",
    "[&::-webkit-slider-thumb]:bg-primary-600",
    "[&::-webkit-slider-thumb]:rounded-full",
    "[&::-moz-range-thumb]:appearance-none",
    "[&::-moz-range-thumb]:w-lg",
    "[&::-moz-range-thumb]:h-lg",
    "[&::-moz-range-thumb]:bg-primary-600",
    "[&::-moz-range-thumb]:rounded-full",
    "bg-no-repeat bg-left",
  ].join(" ");

  if (disabled) {
    ownClassName +=
      " [&::-webkit-slider-thumb]:bg-secondary-200 [&::-moz-range-thumb]:bg-secondary-200 !cursor-not-allowed cursor-not-allowed";
  }

  const progressColor = disabled ? "#D5DDE9" : "#FFCF53";

  return (
    <div className="w-full">
      <input
        type="range"
        name={name}
        id={id}
        min={min}
        max={max}
        step={step}
        value={normalizedValue}
        onChange={(e) => onChange(Number(e.target.value))}
        onPointerUp={(e) => onCommit?.(Number(e.currentTarget.value))}
        onKeyUp={(e) => onCommit?.(Number(e.currentTarget.value))}
        disabled={disabled}
        aria-label={label}
        ref={inputRef}
        className={ownClassName}
        style={{
          backgroundImage: `linear-gradient(to right, ${progressColor}, ${progressColor})`,
        }}
      />
      {!hideLimits && (
        <div className="flex justify-between text-xs text-gray-500">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      )}
    </div>
  );
};
